import { useState, useEffect } from 'react';
import axios from 'axios';

interface Stats {
  n: number;
  wins?: number;
  winRate?: number;
  breakEven?: number;
  pnl?: number;
  roi?: number;
  roiLow?: number;
  roiHigh?: number;
  t?: number;
  brier?: { n: number; model: number; market: number } | null;
}
export interface Money {
  bets: number; staked: number; inPlay: number; openBets: number;
  wins: number; losses: number; wonUsd: number; lostUsd: number; net: number;
}
interface StrategyRow extends Stats {
  strategy: string;
  desc: string;
  open: number;
  money: Money;
  byCategory: Record<string, Stats>;
}
export interface Report {
  stake: number;
  audit: { at: string; checked: number; mismatches: number | null; ok: boolean } | null;
  totals: Money;
  oddsBudget: { budget: number; used: number; remaining: number | null; gamesToday: number } | null;
  strategies: StrategyRow[];
  aiBot: { n: number; pnl: number; roi: number };
}

// An edge only counts once the ROI's ~95% range (±2 standard errors) clears zero on a
// decent sample; until then a good run is indistinguishable from luck.
const MIN_N = 50;
function verdict(s: Stats): { label: string; cls: string } {
  if (!s.n) return { label: 'WAITING', cls: 'edge-neutral' };
  if (s.n < MIN_N) return { label: `EARLY (${s.n}/${MIN_N})`, cls: 'edge-neutral' };
  if ((s.roiLow ?? 0) > 0) return { label: 'EDGE', cls: 'edge-good' };
  if ((s.roiHigh ?? 0) < 0) return { label: 'LOSING', cls: 'edge-bad' };
  return { label: 'UNPROVEN', cls: 'edge-warn' };
}

const pct = (x?: number) => (x == null ? '—' : `${(x * 100).toFixed(1)}%`);
const signedPct = (x?: number) => (x == null ? '—' : `${x >= 0 ? '+' : ''}${(x * 100).toFixed(1)}%`);
const money = (x?: number) => (x == null ? '—' : `${x >= 0 ? '+' : '-'}$${Math.abs(x).toFixed(2)}`);

export default function Experiments({ selected, onSelect, report }: {
  selected?: string | null; onSelect?: (strategy: string | null) => void; report?: Report | null;
} = {}) {
  const [own, setOwn] = useState<Report | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  // Loads its own data unless the parent (Lab) passes the report in.
  useEffect(() => {
    if (report !== undefined) return;
    const load = () => axios.get('/api/experiments').then(r => setOwn(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [report]);

  const data = report !== undefined ? report : own;

  if (!data) return null;
  const rows = [...data.strategies].sort((a, b) => (b.roi ?? -9) - (a.roi ?? -9));
  const totalOpen = rows.reduce((s, r) => s + r.open, 0);

  return (
    <div className="panel edge-panel">
      <div className="panel-header">
        🧪 Strategy Lab
        <span className="panel-count">
          {rows.length} strategies · {totalOpen} open bets
          {data.oddsBudget && ` · odds credits ${data.oddsBudget.used}/${data.oddsBudget.budget} today, ${data.oddsBudget.remaining ?? '?'} left this month`}
        </span>
      </div>
      <div className="edge-detail">
        Paper bets, ${data.stake} flat each, held to resolution, one per event. ROI range is ±2 standard errors:
        an edge counts only when the whole range is above zero over {MIN_N}+ resolved bets.
      </div>
      <div className="exp-table-wrap">
        <table className="exp-table">
          <thead>
            <tr>
              <th>Strategy</th><th>Open</th><th>Resolved</th><th>Win rate</th><th>Need</th>
              <th>P&amp;L</th><th>ROI</th><th>ROI range</th><th>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const v = verdict(r);
              const cats = Object.entries(r.byCategory || {}).filter(([, s]) => s.n > 0);
              return [
                <tr key={r.strategy} className={`exp-row ${selected === r.strategy ? 'exp-selected' : ''}`} title={r.desc}
                  onClick={() => { setOpen(open === r.strategy ? null : r.strategy); onSelect?.(selected === r.strategy ? null : r.strategy); }}>
                  <td><b>{r.strategy}</b>{cats.length > 1 && <span className="exp-caret">{open === r.strategy ? ' ▾' : ' ▸'}</span>}<div className="exp-desc">{r.desc}</div></td>
                  <td>{r.open}</td>
                  <td>{r.n}</td>
                  <td>{pct(r.winRate)}</td>
                  <td>{pct(r.breakEven)}</td>
                  <td className={(r.pnl ?? 0) >= 0 ? 'exp-pos' : 'exp-neg'}>{money(r.pnl)}</td>
                  <td className={(r.roi ?? 0) >= 0 ? 'exp-pos' : 'exp-neg'}>{signedPct(r.roi)}</td>
                  <td className="exp-range">{r.n ? `${signedPct(r.roiLow)} … ${signedPct(r.roiHigh)}` : '—'}</td>
                  <td><span className={`edge-verdict ${v.cls}`}>{v.label}</span></td>
                </tr>,
                open === r.strategy && cats.map(([c, s]) => (
                  <tr key={`${r.strategy}-${c}`} className="exp-sub">
                    <td>↳ {c}</td><td></td><td>{s.n}</td><td>{pct(s.winRate)}</td><td>{pct(s.breakEven)}</td>
                    <td className={(s.pnl ?? 0) >= 0 ? 'exp-pos' : 'exp-neg'}>{money(s.pnl)}</td>
                    <td className={(s.roi ?? 0) >= 0 ? 'exp-pos' : 'exp-neg'}>{signedPct(s.roi)}</td>
                    <td className="exp-range">{`${signedPct(s.roiLow)} … ${signedPct(s.roiHigh)}`}</td>
                    <td><span className={`edge-verdict ${verdict(s).cls}`}>{verdict(s).label}</span></td>
                  </tr>
                )),
              ];
            })}
            <tr className="exp-row exp-ai">
              <td><b>ai_bot</b><div className="exp-desc">Main AI bot (GLM), Kelly-sized with stop-loss — for comparison</div></td>
              <td>—</td><td>{data.aiBot.n}</td><td>—</td><td>—</td>
              <td className={data.aiBot.pnl >= 0 ? 'exp-pos' : 'exp-neg'}>{money(data.aiBot.pnl)}</td>
              <td className={data.aiBot.roi >= 0 ? 'exp-pos' : 'exp-neg'}>{signedPct(data.aiBot.roi)}</td>
              <td>—</td><td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
